// MissionSourceRegistry.ts — Sprint 102: Mission Source Registry™
// BUILD: 2.2.0
//
// PURPOSE: Permanent catalog of every Tier-1 mission platform.
//
// ARCHITECTURE RULE:
//   Mission Sources  = permanent.  A company never disappears from NOMAD.
//   Mission Opportunities = temporary. Zero opps today ≠ company gone.
//
// THE HARVESTER IS AN UPDATER — NOT THE OWNER — OF COMPANY VISIBILITY.
//   The harvester may call updateHarvestStatus().
//   It SHALL NOT remove companies. It SHALL NOT control visibility.
//
// OPERATOR VISIT RECORDS:
//   When an operator manually opens a source, recordSourceVisit() captures:
//   time, date, company, result. This data is available to Learning Engine,
//   Operator Memory, Decision Synthesis, and Mission Intelligence without
//   changing their architecture — they read it via getSourceVisits().
//
// DO NOT MODIFY: Mission Director, Decision Engine, Trust Engine, Learning Engine,
//   Execution Engine, Opportunity Classifier, Ranking, Scoring, Mission Planner,
//   Database Schema, Operator Memory, Business Logic.

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Why harvesting is not (or cannot be) automated for this source.
 *
 * PUBLIC_HARVEST   — connector built and can fetch unauthenticated (future goal)
 * AUTH_REQUIRED    — needs OAuth / API key; server-side proxy required
 * SESSION_REQUIRED — needs operator account session / identity verification
 * PUSH_PLATFORM    — platform pushes work to operator; no pull endpoint exists
 * NO_CONNECTOR     — no connector built yet; operator must visit manually
 * MANUAL_PLATFORM  — by design requires human navigation (e.g. invite-only)
 * UNKNOWN          — status not yet determined
 */
export type MissionSourcePlatformType =
  | 'PUBLIC_HARVEST'
  | 'AUTH_REQUIRED'
  | 'SESSION_REQUIRED'
  | 'PUSH_PLATFORM'
  | 'NO_CONNECTOR'
  | 'MANUAL_PLATFORM'
  | 'UNKNOWN';

/**
 * Result of the most recent harvest attempt for this source.
 * Never null. Never undefined.
 */
export type HarvestStatus =
  | 'SUCCESS'
  | 'NO_OPPORTUNITIES'
  | 'AUTH_REQUIRED'
  | 'SESSION_REQUIRED'
  | 'PUSH_PLATFORM'
  | 'NO_CONNECTOR'
  | 'BLOCKED'
  | 'ERROR'
  | 'UNKNOWN';

/**
 * What the operator found when they manually visited the source dashboard.
 */
export type SourceVisitResult =
  | 'No Work'
  | 'Found Work'
  | 'Applied'
  | 'Working'
  | 'Paid';

/** Full descriptor for one Tier-1 mission source. */
export interface MissionSource {
  /** Unique stable identifier — snake_case, never changes. */
  id: string;

  /** Display name exactly as shown in NOMAD. Matches TIER1_SOURCES canonical name. */
  companyName: string;

  /** 1 = highest priority, 2 = standard, 3 = fallback. */
  tier: 1 | 2 | 3;

  /** Public marketing / homepage URL. */
  websiteUrl: string;

  /** URL to the operator's work dashboard (post-login). */
  dashboardUrl: string;

  /** URL to the operator registration / sign-up page. */
  signupUrl: string;

  /** URL to the login page. */
  loginUrl: string;

  /** Why automated harvesting is blocked or unavailable. */
  platformType: MissionSourcePlatformType;

  /** True if a PUBLIC_HARVEST connector can one day run without credentials. */
  supportsHarvest: boolean;

  /** True if any form of account is required to see work. */
  requiresAuthentication: boolean;

  /** True if the platform screens/qualifies contractors before showing work. */
  requiresQualification: boolean;

  /** True if work is pushed to the operator — no pull API exists. */
  pushPlatform: boolean;

  /** True if a connector class exists in ConnectorRegistry (even if BLOCKED). */
  connectorImplemented: boolean;

  // ── Harvest state — only the harvester writes these fields ─────────────────

  /** Result of the most recent harvest run (automated or simulated). */
  lastHarvestStatus: HarvestStatus;

  /** ISO 8601 timestamp of the most recent harvest attempt. Null = never attempted. */
  lastHarvestAttempt: string | null;

  /** ISO 8601 timestamp of the last harvest that returned ≥1 opportunity. */
  lastSuccessfulHarvest: string | null;

  /** ISO 8601 timestamp of the most recently discovered opportunity from this source. */
  lastOpportunityFound: string | null;

  // ── Static metadata ────────────────────────────────────────────────────────

  /** How often the operator should check this source manually (if not harvestable). */
  recommendedCheckFrequency: string;

  /** Typical hourly rate range, e.g. "$15–$45/hr". */
  averageHourlyRate: string;

  /** Types of work typically available on this platform. */
  typicalWorkTypes: string[];

  /** True if work is accessible on a phone or tablet. */
  mobileFriendly: boolean;

  /** One-line explanation of harvest status / any known blocker. */
  notes: string;
}

/** A record of one operator manual visit to a source dashboard. */
export interface MissionSourceVisit {
  sourceId:    string;
  companyName: string;
  visitedAt:   string; // ISO 8601
  result:      SourceVisitResult;
}

// ─── Storage keys ─────────────────────────────────────────────────────────────

const REGISTRY_OVERRIDE_KEY = 'nomad:source:registry:v1';
const VISITS_KEY             = 'nomad:source:visits:v1';

// ─── Canonical registry ───────────────────────────────────────────────────────
//
// Every Tier-1 company is permanently listed here.
// Harvest failures do NOT remove entries.
// MODIFICATION POLICY: only a dedicated sprint may add or remove sources.

const BASE_REGISTRY: readonly MissionSource[] = [
  // ── AI Evaluation & Creative Writing ────────────────────────────────────────
  {
    id:                      'outlier',
    companyName:             'Outlier',
    tier:                    1,
    websiteUrl:              'https://outlier.ai',
    dashboardUrl:            'https://app.outlier.ai',
    signupUrl:               'https://app.outlier.ai/signup',
    loginUrl:                'https://app.outlier.ai/login',
    platformType:            'SESSION_REQUIRED',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    true,
    lastHarvestStatus:       'BLOCKED',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$20–$80/hr',
    typicalWorkTypes:        ['Creative Writing', 'Code Review', 'AI Evaluation', 'Reasoning Tasks'],
    mobileFriendly:          true,
    notes:                   'Requires government-issued identity verification. Tasks personalised per contractor — generic fetch impossible. Log in to check task availability.',
  },
  {
    id:                      'alignerr',
    companyName:             'Alignerr',
    tier:                    1,
    websiteUrl:              'https://alignerr.com',
    dashboardUrl:            'https://app.alignerr.com',
    signupUrl:               'https://app.alignerr.com/apply',
    loginUrl:                'https://app.alignerr.com/login',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$15–$50/hr',
    typicalWorkTypes:        ['AI Training', 'RLHF', 'Code Review', 'Writing Evaluation'],
    mobileFriendly:          false,
    notes:                   'No connector built. Visit dashboard to check for available projects.',
  },
  {
    id:                      'invisible-technologies',
    companyName:             'Invisible Technologies',
    tier:                    1,
    websiteUrl:              'https://invisibletech.ai',
    dashboardUrl:            'https://invisibletech.ai/careers',
    signupUrl:               'https://invisibletech.ai/careers',
    loginUrl:                'https://invisibletech.ai',
    platformType:            'MANUAL_PLATFORM',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            true,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Weekly',
    averageHourlyRate:       '$15–$35/hr',
    typicalWorkTypes:        ['AI Operations', 'Process Automation', 'Data Labeling'],
    mobileFriendly:          false,
    notes:                   'Application and vetting process required. Work assigned after approval.',
  },

  // ── Research Studies ─────────────────────────────────────────────────────────
  {
    id:                      'prolific',
    companyName:             'Prolific',
    tier:                    1,
    websiteUrl:              'https://www.prolific.com/participants',
    dashboardUrl:            'https://app.prolific.com',
    signupUrl:               'https://app.prolific.com/register',
    loginUrl:                'https://app.prolific.com/login',
    platformType:            'AUTH_REQUIRED',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    true,
    lastHarvestStatus:       'BLOCKED',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Multiple times daily',
    averageHourlyRate:       '£9–£15/hr',
    typicalWorkTypes:        ['Academic Research', 'Survey Studies', 'Cognitive Tasks'],
    mobileFriendly:          false,
    notes:                   'OAuth 2.0 API — server-side proxy required. Log in to dashboard to see available studies.',
  },
  {
    id:                      'cloudresearch-connect',
    companyName:             'CloudResearch Connect',
    tier:                    1,
    websiteUrl:              'https://www.cloudresearch.com',
    dashboardUrl:            'https://connect.cloudresearch.com/participant',
    signupUrl:               'https://connect.cloudresearch.com/participant/register',
    loginUrl:                'https://connect.cloudresearch.com/participant/login',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Multiple times daily',
    averageHourlyRate:       '$12–$20/hr',
    typicalWorkTypes:        ['Survey Studies', 'Academic Research', 'Cognitive Tasks'],
    mobileFriendly:          false,
    notes:                   'No connector built. Studies typically posted daily — check dashboard regularly.',
  },

  // ── User Testing & UX Research ───────────────────────────────────────────────
  {
    id:                      'usertesting',
    companyName:             'UserTesting',
    tier:                    1,
    websiteUrl:              'https://www.usertesting.com/get-paid-to-test',
    dashboardUrl:            'https://app.usertesting.com',
    signupUrl:               'https://app.usertesting.com/sign_up',
    loginUrl:                'https://app.usertesting.com/login',
    platformType:            'PUSH_PLATFORM',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            true,
    connectorImplemented:    true,
    lastHarvestStatus:       'PUSH_PLATFORM',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Platform notifies by email',
    averageHourlyRate:       '$30/hr (≈$10/test)',
    typicalWorkTypes:        ['Website Usability', 'App Testing', 'Prototype Review'],
    mobileFriendly:          true,
    notes:                   'Tests assigned automatically by UserTesting based on screener match. No pull API exists — platform pushes opportunities.',
  },
  {
    id:                      'user-interviews',
    companyName:             'User Interviews',
    tier:                    1,
    websiteUrl:              'https://userinterviews.com',
    dashboardUrl:            'https://app.userinterviews.com',
    signupUrl:               'https://app.userinterviews.com/participants/sign_up',
    loginUrl:                'https://app.userinterviews.com/participants/sign_in',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$50–$200/hr (interview-based)',
    typicalWorkTypes:        ['User Interviews', 'Concept Testing', 'Diary Studies'],
    mobileFriendly:          false,
    notes:                   'No connector built. High-value interviews — check dashboard and enable email alerts.',
  },
  {
    id:                      'intellizoom',
    companyName:             'IntelliZoom',
    tier:                    1,
    websiteUrl:              'https://network.usertesting.com',
    dashboardUrl:            'https://network.usertesting.com',
    signupUrl:               'https://network.usertesting.com/s/sign-up',
    loginUrl:                'https://network.usertesting.com/login',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$30/hr (≈$10/test)',
    typicalWorkTypes:        ['Website Usability', 'App Testing', 'Remote UX Research'],
    mobileFriendly:          false,
    notes:                   'No connector built. Tests available daily — visit dashboard to participate.',
  },
  {
    id:                      'userlytics',
    companyName:             'Userlytics',
    tier:                    1,
    websiteUrl:              'https://www.userlytics.com/user-experience-research/paid-ux-testing/',
    dashboardUrl:            'https://app.userlytics.com',
    signupUrl:               'https://www.userlytics.com/user-experience-research/paid-ux-testing/',
    loginUrl:                'https://app.userlytics.com/login',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$5–$90/test',
    typicalWorkTypes:        ['UX Testing', 'Prototype Review', 'Competitive Analysis'],
    mobileFriendly:          true,
    notes:                   'No connector built. Tests range from quick 5-min clips to full 60-min sessions.',
  },
  {
    id:                      'trymata',
    companyName:             'Trymata',
    tier:                    1,
    websiteUrl:              'https://trymata.com',
    dashboardUrl:            'https://trymata.com',
    signupUrl:               'https://trymata.com',
    loginUrl:                'https://trymata.com',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$10/20-min test',
    typicalWorkTypes:        ['Website Testing', 'App Review', 'UX Feedback'],
    mobileFriendly:          true,
    notes:                   'Formerly TryMyUI. App portal migrated to QuestionPro UX (ux.questionpro.com) — use trymata.com as the canonical public entry point. No connector built.',
  },
  {
    id:                      'playbookux',
    companyName:             'PlaybookUX',
    tier:                    1,
    websiteUrl:              'https://playbookux.com',
    dashboardUrl:            'https://app.playbookux.com',
    signupUrl:               'https://app.playbookux.com/register',
    loginUrl:                'https://app.playbookux.com/login',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Several times per week',
    averageHourlyRate:       '$10–$20/test',
    typicalWorkTypes:        ['Usability Testing', 'Card Sorting', 'Prototype Feedback'],
    mobileFriendly:          false,
    notes:                   'No connector built. Moderated and unmoderated studies available.',
  },
  {
    id:                      'testingtime',
    companyName:             'TestingTime',
    tier:                    1,
    websiteUrl:              'https://www.testingtime.com/en/become-a-tester/',
    dashboardUrl:            'https://www.testingtime.com/en/become-a-tester/',
    signupUrl:               'https://www.testingtime.com/en/become-a-tester/',
    loginUrl:                'https://www.testingtime.com',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Several times per week',
    averageHourlyRate:       '€30–€90/test',
    typicalWorkTypes:        ['Moderated User Testing', 'UX Interviews', 'Prototype Review'],
    mobileFriendly:          false,
    notes:                   'No connector built. European-focused platform — higher pay for moderated sessions. Root domain testingtime.com returns 403 from bots (Cloudflare protection); www.testingtime.com/en/become-a-tester/ verified HTTP 200.',
  },
  {
    id:                      'userfeel',
    companyName:             'Userfeel',
    tier:                    1,
    websiteUrl:              'https://userfeel.com',
    dashboardUrl:            'https://app.userfeel.com',
    signupUrl:               'https://app.userfeel.com/tester_register',
    loginUrl:                'https://app.userfeel.com/login',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Several times per week',
    averageHourlyRate:       '$10/test',
    typicalWorkTypes:        ['Multilingual Testing', 'Website Review', 'App Testing'],
    mobileFriendly:          true,
    notes:                   'No connector built. Requires passing a qualification test. Multilingual testers preferred.',
  },
  {
    id:                      'pingpong',
    companyName:             'PingPong',
    tier:                    1,
    websiteUrl:              'https://www.hotjar.com/engage/participant-pool/',
    dashboardUrl:            'https://engage.hotjar.com',
    signupUrl:               'https://www.hotjar.com/engage/participant-pool/',
    loginUrl:                'https://engage.hotjar.com',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Several times per week',
    averageHourlyRate:       '$25–$75/session',
    typicalWorkTypes:        ['Remote UX Research', 'User Interviews', 'Usability Testing'],
    mobileFriendly:          false,
    notes:                   'No connector built. High-paying sessions — check regularly for openings.',
  },
  {
    id:                      'conversion-crimes',
    companyName:             'Conversion Crimes',
    tier:                    1,
    websiteUrl:              'https://conversioncrimes.com',
    dashboardUrl:            'https://app.conversioncrimes.com',
    signupUrl:               'https://app.conversioncrimes.com/register',
    loginUrl:                'https://app.conversioncrimes.com/login',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Several times per week',
    averageHourlyRate:       '$10–$15/test',
    typicalWorkTypes:        ['CRO Testing', 'Landing Page Review', 'Conversion Feedback'],
    mobileFriendly:          true,
    notes:                   'No connector built. Focused on conversion rate optimization tests.',
  },

  // ── AI Training & Data Annotation ────────────────────────────────────────────
  {
    id:                      'appen',
    companyName:             'Appen',
    tier:                    1,
    websiteUrl:              'https://www.appen.com',
    dashboardUrl:            'https://crowdgen.com',
    signupUrl:               'https://crowdgen.com',
    loginUrl:                'https://crowdgen.com',
    platformType:            'SESSION_REQUIRED',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    true,
    lastHarvestStatus:       'BLOCKED',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$15–$45/hr',
    typicalWorkTypes:        ['Data Annotation', 'Search Evaluation', 'Transcription', 'AI Training'],
    mobileFriendly:          false,
    notes:                   'Appen\'s crowd platform migrated to CrowdGen (crowdgen.com) in 2024 — connect.appen.com is 503. New workers register at crowdgen.com. No public API — log in to check available jobs.',
  },
  {
    id:                      'telus-digital',
    companyName:             'TELUS Digital',
    tier:                    1,
    websiteUrl:              'https://www.telusinternational.com',
    dashboardUrl:            'https://www.telusinternational.com/solutions/digital-solutions/ai-data',
    signupUrl:               'https://www.telusinternational.com/solutions/digital-solutions/ai-data',
    loginUrl:                'https://www.telusinternational.com/solutions/digital-solutions/ai-data',
    platformType:            'SESSION_REQUIRED',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    true,
    lastHarvestStatus:       'BLOCKED',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$15–$40/hr',
    typicalWorkTypes:        ['Search Evaluation', 'Content Moderation', 'AI Data Annotation'],
    mobileFriendly:          false,
    notes:                   'Formerly Lionbridge AI. Contractor approval required. Log in to check task availability.',
  },
  {
    id:                      'welocalize',
    companyName:             'Welocalize',
    tier:                    1,
    websiteUrl:              'https://www.welocalize.com',
    dashboardUrl:            'https://www.welocalize.com',
    signupUrl:               'https://www.welocalize.com',
    loginUrl:                'https://www.welocalize.com',
    platformType:            'AUTH_REQUIRED',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    true,
    lastHarvestStatus:       'BLOCKED',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$12–$35/hr',
    typicalWorkTypes:        ['Localization', 'Search Relevance', 'Transcription', 'Translation'],
    mobileFriendly:          false,
    notes:                   'Skill testing and approval required. The /about/careers/ page is 404 (Jul 2026) — use homepage. Log in to view available projects.',
  },
  {
    id:                      'toloka',
    companyName:             'Toloka',
    tier:                    1,
    websiteUrl:              'https://toloka.ai',
    dashboardUrl:            'https://mindrift.ai',
    signupUrl:               'https://mindrift.ai',
    loginUrl:                'https://mindrift.ai/login',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$5–$20/hr',
    typicalWorkTypes:        ['Data Labeling', 'Image Annotation', 'NLP Tasks', 'Microtasks'],
    mobileFriendly:          true,
    notes:                   'No connector built. Yandex-backed microtask platform — high task volume.',
  },
  {
    id:                      'rapidworkers',
    companyName:             'RapidWorkers',
    tier:                    1,
    websiteUrl:              'https://rapidworkers.com',
    dashboardUrl:            'https://rapidworkers.com/dashboard',
    signupUrl:               'https://rapidworkers.com/register',
    loginUrl:                'https://rapidworkers.com/login',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$0.01–$5/task',
    typicalWorkTypes:        ['Microtasks', 'Social Tasks', 'Search Tasks'],
    mobileFriendly:          true,
    notes:                   'No connector built. High volume, lower pay — useful for building task history.',
  },

  // ── Language & Localization AI ───────────────────────────────────────────────
  {
    id:                      'transperfect-dataforce',
    companyName:             'TransPerfect DataForce',
    tier:                    1,
    websiteUrl:              'https://dataforce.transperfect.com',
    dashboardUrl:            'https://dataforce.transperfect.com',
    signupUrl:               'https://dataforce.transperfect.com/apply',
    loginUrl:                'https://dataforce.transperfect.com/login',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Weekly',
    averageHourlyRate:       '$12–$30/hr',
    typicalWorkTypes:        ['Linguistic Data', 'Speech Collection', 'Translation', 'Annotation'],
    mobileFriendly:          false,
    notes:                   'No connector built. Language specialist tasks — check for project availability.',
  },
  {
    id:                      'lxt',
    companyName:             'LXT',
    tier:                    1,
    websiteUrl:              'https://www.lxt.ai',
    dashboardUrl:            'https://www.lxt.ai/become-a-contributor',
    signupUrl:               'https://www.lxt.ai/become-a-contributor',
    loginUrl:                'https://www.lxt.ai/login',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Weekly',
    averageHourlyRate:       '$10–$25/hr',
    typicalWorkTypes:        ['Speech Data', 'Audio Recording', 'Transcription', 'Language Evaluation'],
    mobileFriendly:          true,
    notes:                   'No connector built. Specialises in speech and audio AI training data.',
  },
  {
    id:                      'pactera-edge',
    companyName:             'Pactera EDGE',
    tier:                    1,
    websiteUrl:              'https://www.pacteraedge.com',
    dashboardUrl:            'https://www.pacteraedge.com/join',
    signupUrl:               'https://www.pacteraedge.com/join',
    loginUrl:                'https://www.pacteraedge.com',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Weekly',
    averageHourlyRate:       '$12–$28/hr',
    typicalWorkTypes:        ['AI Training Data', 'NLP', 'Content Annotation', 'Search Relevance'],
    mobileFriendly:          false,
    notes:                   'No connector built. Enterprise AI data platform — check for available project cycles.',
  },
  {
    id:                      'rws',
    companyName:             'RWS',
    tier:                    1,
    websiteUrl:              'https://www.rws.com',
    dashboardUrl:            'https://www.rws.com/linguistic-solutions/moravia',
    signupUrl:               'https://www.rws.com/linguistic-solutions/moravia',
    loginUrl:                'https://www.rws.com/login',
    platformType:            'AUTH_REQUIRED',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Weekly',
    averageHourlyRate:       '$15–$35/hr',
    typicalWorkTypes:        ['Translation', 'Localization', 'Linguistic QA', 'Content Review'],
    mobileFriendly:          false,
    notes:                   'No connector built. Specialist linguistic tasks — qualification required.',
  },
  {
    id:                      'e2f',
    companyName:             'e2f',
    tier:                    1,
    websiteUrl:              'https://www.e2f.com',
    dashboardUrl:            'https://www.e2f.com/crowd/',
    signupUrl:               'https://www.e2f.com/crowd/',
    loginUrl:                'https://www.e2f.com',
    platformType:            'AUTH_REQUIRED',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Weekly',
    averageHourlyRate:       '$12–$30/hr',
    typicalWorkTypes:        ['Data Collection', 'AI Training', 'Transcription', 'Translation'],
    mobileFriendly:          false,
    notes:                   'No connector built. Application process required before project access.',
  },

  // ── Microtask & Crowdsourcing Platforms ──────────────────────────────────────
  {
    id:                      'dataannotation',
    companyName:             'DataAnnotation',
    tier:                    1,
    websiteUrl:              'https://www.dataannotation.tech',
    dashboardUrl:            'https://www.dataannotation.tech',
    signupUrl:               'https://www.dataannotation.tech',
    loginUrl:                'https://www.dataannotation.tech',
    platformType:            'SESSION_REQUIRED',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    true,
    lastHarvestStatus:       'BLOCKED',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$20–$40/hr',
    typicalWorkTypes:        ['AI Training', 'RLHF', 'Code Review', 'Creative Writing'],
    mobileFriendly:          false,
    notes:                   'Manual admin approval required before tasks are visible. Task invitations delivered by email after approval. /users/sign_in and /users/sign_up Rails paths return 404 (Jul 2026) — use homepage. Connector built — unblock path: authenticated proxy with approved account.',
  },
  {
    id:                      'respondent',
    companyName:             'Respondent',
    tier:                    1,
    websiteUrl:              'https://www.respondent.io/become-a-participant',
    dashboardUrl:            'https://app.respondent.io',
    signupUrl:               'https://app.respondent.io/participants/sign_up',
    loginUrl:                'https://app.respondent.io/sign_in',
    platformType:            'SESSION_REQUIRED',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            true,
    connectorImplemented:    true,
    lastHarvestStatus:       'BLOCKED',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$50–$200/hr',
    typicalWorkTypes:        ['User Research', 'Concept Testing', 'Expert Interviews', 'Diary Studies'],
    mobileFriendly:          false,
    notes:                   'Study invitations matched by screener profile. High-value sessions — keep profile updated and enable email alerts. Connector built — needs authenticated proxy.',
  },
  {
    id:                      'mturk',
    companyName:             'Amazon Mechanical Turk',
    tier:                    1,
    websiteUrl:              'https://www.mturk.com/worker',
    dashboardUrl:            'https://www.mturk.com/worker',
    signupUrl:               'https://www.mturk.com/get-started',
    loginUrl:                'https://www.mturk.com/signin',
    platformType:            'AUTH_REQUIRED',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    true,
    lastHarvestStatus:       'BLOCKED',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Multiple times daily',
    averageHourlyRate:       '$3–$15/hr',
    typicalWorkTypes:        ['Microtasks', 'Data Collection', 'Transcription', 'Image Labeling', 'Survey'],
    mobileFriendly:          false,
    notes:                   'AWS SigV4 authentication required — server-side proxy with AWS credentials needed. Connector built — high HIT volume. worker.mturk.com redirects to Amazon SSO login; use www.mturk.com/worker for the public landing page.',
  },
  {
    id:                      'clickworker',
    companyName:             'Clickworker',
    tier:                    1,
    websiteUrl:              'https://www.clickworker.com/become-a-clickworker/',
    dashboardUrl:            'https://workplace.clickworker.com',
    signupUrl:               'https://www.clickworker.com/become-a-clickworker/',
    loginUrl:                'https://workplace.clickworker.com/en/clickworker',
    platformType:            'AUTH_REQUIRED',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    true,
    lastHarvestStatus:       'BLOCKED',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$5–$25/hr',
    typicalWorkTypes:        ['Microtasks', 'Data Entry', 'Content Creation', 'Proofreading', 'App Testing'],
    mobileFriendly:          true,
    notes:                   'WORK API requires enterprise partner key — visit workplace dashboard to browse available jobs. Connector built — unblock path: server-side proxy with enterprise API key.',
  },
  {
    id:                      'oneforma',
    companyName:             'OneForma',
    tier:                    1,
    websiteUrl:              'https://www.oneforma.com',
    dashboardUrl:            'https://www.oneforma.com',
    signupUrl:               'https://www.oneforma.com/work-with-us/',
    loginUrl:                'https://www.oneforma.com',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$10–$30/hr',
    typicalWorkTypes:        ['Transcription', 'Translation', 'Data Annotation', 'Content Moderation'],
    mobileFriendly:          false,
    notes:                   'Formerly Lionbridge crowd platform (now Centific). Qualification tasks required. No connector built — visit crowd dashboard directly.',
  },
  {
    id:                      'remotasks',
    companyName:             'Remotasks',
    tier:                    1,
    websiteUrl:              'https://www.remotasks.com',
    dashboardUrl:            'https://remotasks.com/en/tasks',
    signupUrl:               'https://remotasks.com/en/sign-up',
    loginUrl:                'https://remotasks.com/en/login',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$2–$20/hr',
    typicalWorkTypes:        ['AI Training', 'Data Labeling', '3D Annotation', 'Image Tagging', 'RLHF'],
    mobileFriendly:          false,
    notes:                   'Scale AI subsidiary. Training bootcamp required before task access. No connector built — check task availability after bootcamp completion.',
  },
  {
    id:                      'microworkers',
    companyName:             'Microworkers',
    tier:                    1,
    websiteUrl:              'https://microworkers.com',
    dashboardUrl:            'https://www.microworkers.com',
    signupUrl:               'https://microworkers.com/register.php',
    loginUrl:                'https://microworkers.com/login.php',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$1–$10/hr',
    typicalWorkTypes:        ['Microtasks', 'Web Research', 'Data Collection', 'Social Tasks'],
    mobileFriendly:          true,
    notes:                   'High-volume microtask marketplace. Lower pay — useful for building task history. No connector built.',
  },
  {
    id:                      'timebucks',
    companyName:             'TimeBucks',
    tier:                    1,
    websiteUrl:              'https://timebucks.com',
    dashboardUrl:            'https://timebucks.com/dashboard',
    signupUrl:               'https://timebucks.com/register',
    loginUrl:                'https://timebucks.com/login',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   false,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$1–$5/hr',
    typicalWorkTypes:        ['Surveys', 'Video Watching', 'Social Tasks', 'Offer Walls'],
    mobileFriendly:          true,
    notes:                   'Survey and offer platform. Lower pay — useful as supplement. No connector built.',
  },
  {
    id:                      'lionbridge',
    companyName:             'Lionbridge',
    tier:                    1,
    websiteUrl:              'https://www.lionbridge.com/join-our-team/',
    dashboardUrl:            'https://www.lionbridge.com/join-our-team/',
    signupUrl:               'https://www.lionbridge.com/join-our-team/',
    loginUrl:                'https://www.lionbridge.com/join-our-team/',
    platformType:            'MANUAL_PLATFORM',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Weekly',
    averageHourlyRate:       '$15–$40/hr',
    typicalWorkTypes:        ['Search Evaluation', 'Translation', 'Content Rating', 'AI Annotation'],
    mobileFriendly:          false,
    notes:                   'Legacy platform — AI evaluator tasks now primarily via TELUS Digital (formerly Lionbridge AI). LionTask portal (login.liontask.com, cloud.liontask.com) is dead as of Jul 2026 — use www.lionbridge.com/join-our-team/ for job applications.',
  },
  {
    id:                      'test-io',
    companyName:             'Test IO',
    tier:                    1,
    websiteUrl:              'https://members.test.io/sign_in',
    dashboardUrl:            'https://members.test.io/sign_in',
    signupUrl:               'https://members.test.io/sign_in',
    loginUrl:                'https://members.test.io/sign_in',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$15–$50/project',
    typicalWorkTypes:        ['Manual Testing', 'Bug Reporting', 'Functional Testing', 'Exploratory Testing'],
    mobileFriendly:          false,
    notes:                   'Freelance software testing platform — pay per accepted bug report. Tester rating affects project access. test.io root returns 403 (CF bot protection); members.test.io/sign_in is the verified public entry point. No connector built.',
  },
  {
    id:                      'utest',
    companyName:             'uTest',
    tier:                    1,
    websiteUrl:              'https://www.utest.com',
    dashboardUrl:            'https://app.utest.com',
    signupUrl:               'https://www.utest.com/signup',
    loginUrl:                'https://www.utest.com/login',
    platformType:            'NO_CONNECTOR',
    supportsHarvest:         false,
    requiresAuthentication:  true,
    requiresQualification:   true,
    pushPlatform:            false,
    connectorImplemented:    false,
    lastHarvestStatus:       'NO_CONNECTOR',
    lastHarvestAttempt:      null,
    lastSuccessfulHarvest:   null,
    lastOpportunityFound:    null,
    recommendedCheckFrequency: 'Daily',
    averageHourlyRate:       '$5–$50/project',
    typicalWorkTypes:        ['Software Testing', 'Bug Reports', 'Mobile Testing', 'UAT', 'Localization Testing'],
    mobileFriendly:          true,
    notes:                   'Now part of Applause. Higher tester ratings unlock more projects. No connector built.',
  },
] as const;

// ─── Persistence — dynamic fields only ───────────────────────────────────────
//
// BASE_REGISTRY contains all static + initial dynamic values.
// At runtime, updateHarvestStatus() persists overrides to localStorage.
// getAllSources() merges base + overrides so the UI always sees the latest state.

type HarvestOverride = Pick<MissionSource,
  'lastHarvestStatus' | 'lastHarvestAttempt' | 'lastSuccessfulHarvest' | 'lastOpportunityFound'
>;

function loadOverrides(): Record<string, HarvestOverride> {
  try {
    const raw = localStorage.getItem(REGISTRY_OVERRIDE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, HarvestOverride>) : {};
  } catch {
    return {};
  }
}

function saveOverrides(overrides: Record<string, HarvestOverride>): void {
  try {
    localStorage.setItem(REGISTRY_OVERRIDE_KEY, JSON.stringify(overrides));
  } catch { /* localStorage unavailable — overrides are in-memory only */ }
}

// ─── Public read API ──────────────────────────────────────────────────────────

/** Return all registered sources, merged with any persisted harvest overrides. */
export function getAllSources(): MissionSource[] {
  const overrides = loadOverrides();
  return BASE_REGISTRY.map(src => {
    const o = overrides[src.id];
    return o ? { ...src, ...o } : { ...src };
  });
}

/** Return one source by id, or undefined if not found. */
export function getSourceById(id: string): MissionSource | undefined {
  return getAllSources().find(s => s.id === id);
}

/**
 * Fuzzy lookup of a registry source by company name.
 *
 * Handles common cases where the company name stored in an opportunity record
 * differs from the registry entry:
 *   • TLD suffixes  — "Outlier.ai" → "Outlier", "Respondent.io" → "Respondent"
 *   • Known aliases — "TELUS International AI" → "telus-digital"
 *   • Normalised ID — "DataAnnotation.tech" → id "dataannotation"
 *
 * Returns undefined only when no reasonable match exists.
 */
export function findSourceByCompanyName(name: string): MissionSource | undefined {
  if (!name || !name.trim()) return undefined;

  const ALIASES: Record<string, string> = {
    'telus international ai':  'telus-digital',
    'telus international':     'telus-digital',
    'amazon mechanical turk':  'mturk',
    'mechanical turk':         'mturk',
    'mturk':                   'mturk',
  };

  const sources = getAllSources();
  const raw     = name.trim();
  const lower   = raw.toLowerCase();

  // 1. Alias lookup (fastest path for known mismatches)
  const aliasId = ALIASES[lower];
  if (aliasId) {
    const byAlias = sources.find(s => s.id === aliasId);
    if (byAlias) return byAlias;
  }

  // 2. Exact match by companyName (case-insensitive)
  const exact = sources.find(s => s.companyName.toLowerCase() === lower);
  if (exact) return exact;

  // 3. Strip common TLD suffixes and retry
  const stripped = lower.replace(/\.(ai|io|com|net|org|tech|co|app|biz)$/i, '').trim();
  if (stripped !== lower) {
    const byName = sources.find(s => s.companyName.toLowerCase() === stripped);
    if (byName) return byName;
    // Try by normalised ID (no hyphens, no spaces)
    const strippedId = stripped.replace(/[\s-]+/g, '');
    const byId = sources.find(s => s.id.replace(/-/g, '') === strippedId);
    if (byId) return byId;
  }

  // 4. Try normalised ID from the original name
  const candidateId = lower.replace(/[^a-z0-9]/g, '');
  const byRawId = sources.find(s => s.id.replace(/-/g, '') === candidateId);
  if (byRawId) return byRawId;

  // 5. Partial match — companyName contains, or is contained by, the query
  const partial = sources.find(s => {
    const sn = s.companyName.toLowerCase();
    return sn.includes(stripped) || stripped.includes(sn);
  });
  return partial;
}

/**
 * Return sources that have NO active inbox items from this company.
 *
 * `activeCompanyNames` — a normalised Set of company names already present
 * in the inbox (non-Ignored, non-Archived). The caller builds this from
 * inboxItems in Home.tsx — the registry never imports React or inbox services.
 */
export function getSourcesWithoutOpportunities(
  activeCompanyNames: ReadonlySet<string>,
): MissionSource[] {
  return getAllSources().filter(src => {
    const norm = src.companyName.toLowerCase().trim();
    return !activeCompanyNames.has(norm);
  });
}

// ─── Harvester write API ──────────────────────────────────────────────────────
//
// ONLY the harvester (opportunityPipeline.ts) calls these.
// No UI component may call updateHarvestStatus() directly.

/**
 * Update the harvest state for one source.
 * Called by the pipeline after each connector run.
 */
export function updateHarvestStatus(
  sourceId:      string,
  status:        HarvestStatus,
  foundOpportunity?: boolean,
): void {
  const overrides = loadOverrides();
  const now = new Date().toISOString();
  const existing = overrides[sourceId] ?? {
    lastHarvestStatus:     'UNKNOWN' as HarvestStatus,
    lastHarvestAttempt:    null,
    lastSuccessfulHarvest: null,
    lastOpportunityFound:  null,
  };

  overrides[sourceId] = {
    lastHarvestStatus:     status,
    lastHarvestAttempt:    now,
    lastSuccessfulHarvest: (status === 'SUCCESS' || foundOpportunity)
      ? now
      : existing.lastSuccessfulHarvest,
    lastOpportunityFound:  foundOpportunity
      ? now
      : existing.lastOpportunityFound,
  };

  saveOverrides(overrides);
}

// ─── Operator visit API ───────────────────────────────────────────────────────

/**
 * Record that the operator manually visited a source dashboard.
 * Defaults result to 'No Work' — the operator may update this later.
 *
 * This data is accessible to:
 *   Learning Engine, Operator Memory, Decision Synthesis, Mission Intelligence
 * via getSourceVisits() — no architectural changes to those engines required.
 */
export function recordSourceVisit(
  sourceId:    string,
  companyName: string,
  result:      SourceVisitResult = 'No Work',
): void {
  const visits = loadVisits();
  visits.push({ sourceId, companyName, visitedAt: new Date().toISOString(), result });
  try {
    localStorage.setItem(VISITS_KEY, JSON.stringify(visits));
  } catch { /* silently skip */ }
}

/** Load all stored visit records. Pass sourceId to filter to one source. */
export function getSourceVisits(sourceId?: string): MissionSourceVisit[] {
  const visits = loadVisits();
  return sourceId ? visits.filter(v => v.sourceId === sourceId) : visits;
}

function loadVisits(): MissionSourceVisit[] {
  try {
    const raw = localStorage.getItem(VISITS_KEY);
    return raw ? (JSON.parse(raw) as MissionSourceVisit[]) : [];
  } catch {
    return [];
  }
}

// ─── UI helpers ───────────────────────────────────────────────────────────────

/** Human-readable label for a HarvestStatus value. */
export function harvestStatusLabel(status: HarvestStatus): string {
  switch (status) {
    case 'SUCCESS':          return 'HARVESTING';
    case 'NO_OPPORTUNITIES': return 'NO OPPORTUNITIES TODAY';
    case 'AUTH_REQUIRED':    return 'AUTH REQUIRED';
    case 'SESSION_REQUIRED': return 'LOGIN REQUIRED';
    case 'PUSH_PLATFORM':    return 'PUSH PLATFORM';
    case 'NO_CONNECTOR':     return 'NO CONNECTOR';
    case 'BLOCKED':          return 'CONNECTOR BLOCKED';
    case 'ERROR':            return 'HARVEST ERROR';
    case 'UNKNOWN':          return 'NOT YET CHECKED';
  }
}

/** One-line reason explaining the current harvest status. */
export function harvestStatusReason(source: MissionSource): string {
  switch (source.lastHarvestStatus) {
    case 'SUCCESS':
      return 'Opportunities are being harvested automatically.';
    case 'NO_OPPORTUNITIES':
      return 'Harvest ran successfully — no live opportunities today.';
    case 'AUTH_REQUIRED':
    case 'BLOCKED':
      return source.platformType === 'SESSION_REQUIRED'
        ? 'Account login and identity verification required. Visit your dashboard directly.'
        : 'API authentication required. Visit your dashboard to see available work.';
    case 'SESSION_REQUIRED':
      return 'Contractor account session required. Log in to check task availability.';
    case 'PUSH_PLATFORM':
      return `${source.companyName} assigns work to you automatically — there is no listing to browse.`;
    case 'NO_CONNECTOR':
      return 'No automated connector built yet. Visit the platform directly to check for work.';
    case 'ERROR':
      return 'Harvest encountered an error. Visit the platform directly.';
    case 'UNKNOWN':
      return 'Harvest has not been attempted yet.';
  }
}

/** Short action label for the operator's recommended next step. */
export function recommendedActionLabel(source: MissionSource): string {
  switch (source.platformType) {
    case 'PUBLIC_HARVEST':   return 'VIEW OPPORTUNITIES';
    case 'AUTH_REQUIRED':
    case 'SESSION_REQUIRED': return `OPEN ${source.companyName.toUpperCase()}`;
    case 'PUSH_PLATFORM':    return `OPEN ${source.companyName.toUpperCase()}`;
    case 'NO_CONNECTOR':     return 'VISIT WEBSITE';
    case 'MANUAL_PLATFORM':  return 'VISIT WEBSITE';
    case 'UNKNOWN':          return 'VISIT WEBSITE';
  }
}

/** URL to open when the operator clicks the recommended action. */
export function recommendedActionUrl(source: MissionSource): string {
  switch (source.platformType) {
    case 'AUTH_REQUIRED':
    case 'SESSION_REQUIRED':
    case 'PUSH_PLATFORM':    return source.dashboardUrl;
    default:                 return source.websiteUrl;
  }
}
